import { CheckIcon, LockClosedIcon } from '@radix-ui/react-icons'
import { cn } from 'lib/utils'
import { Button } from 'primitives/button'
import { type Dispatch, type DragEvent, type SetStateAction, useRef, useState } from 'react'
import type {
  DataTableCellValue,
  DataTableColumn,
  DataTableInstance,
  DataTableRowData,
} from '../../types'
import { reorderColumn } from '../../utils/column.utils'
import { dataVariable } from '../../utils/style.utils'
import { GrabHandleButton } from '../buttons/grab-handle-button'
import classes from './column-visibility-menu-item.module.css'

interface Props<TData extends DataTableRowData, TValue = DataTableCellValue> {
  allColumns: DataTableColumn<TData>[]
  column: DataTableColumn<TData, TValue>
  hoveredColumn: DataTableColumn<TData> | null
  setHoveredColumn: Dispatch<SetStateAction<DataTableColumn<TData> | null>>
  table: DataTableInstance<TData>
}

export const DataTableShowHideColumnsMenuItems = <TData extends DataTableRowData>({
  allColumns,
  column,
  hoveredColumn,
  setHoveredColumn,

  table,
}: Props<TData>) => {
  const {
    getState,
    options: { enableColumnOrdering, enableHiding, enableColumnPinning },
    setColumnOrder,
  } = table
  const { columnOrder } = getState()
  const { columnDef } = column
  const { columnDefType } = columnDef

  const canHide = column.getCanHide()

  const isColumnPinned =
    enableColumnPinning && columnDef.columnDefType !== 'group' && column.getIsPinned()

  const switchChecked =
    (columnDefType !== 'group' && column.getIsVisible()) ||
    (columnDefType === 'group' && column.getLeafColumns().some((col) => col.getIsVisible()))

  const handleToggleColumnHidden = (columnToHide: DataTableColumn<TData>) => {
    if (column.getIsPinned()) return
    if (columnDefType === 'group') {
      columnToHide?.columns?.forEach?.((childColumn: DataTableColumn<TData>) => {
        childColumn.toggleVisibility(!switchChecked)
      })
    } else {
      columnToHide.toggleVisibility()
    }
  }

  const menuItemRef = useRef<HTMLDivElement>(null)

  const [isDragging, setIsDragging] = useState(false)

  const handleDragStart = (e: DragEvent<HTMLButtonElement>) => {
    setIsDragging(true)
    e.dataTransfer.setDragImage(menuItemRef.current as HTMLElement, 0, 0)
  }

  const handleDragEnd = () => {
    setIsDragging(false)
    setHoveredColumn(null)
    if (hoveredColumn) {
      setColumnOrder(reorderColumn(column, hoveredColumn, columnOrder))
    }
  }

  const handleDragEnter = () => {
    if (!isDragging && columnDef.enableColumnOrdering !== false) {
      setHoveredColumn(column)
    }
  }

  if (!columnDef.header || columnDef.visibleInShowHideMenu === false) {
    return null
  }

  return (
    <>
      <div
        className={classes.root}
        onDragEnter={handleDragEnter}
        ref={menuItemRef}
        {...dataVariable('dragging', isDragging)}
        {...dataVariable('order-hovered', hoveredColumn?.id === column.id)}
        onClick={() => handleToggleColumnHidden(column)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleToggleColumnHidden(column)
          }
        }}
      >
        <div className="flex items-center flex-nowrap justify-between">
          <div>
            <div className="flex gap-2 items-center">
              {columnDef.enableColumnOrdering !== false && canHide && !isColumnPinned ? (
                enableHiding ? (
                  <Button variant="ghost" size="icon" className="h-4 w-4">
                    {column.getIsVisible() ? <CheckIcon /> : null}
                  </Button>
                ) : null
              ) : (
                <LockClosedIcon className="h-4 w-4 text-tertiary" />
              )}

              <p className={cn('self-center font-inter text-s-400 normal-case tracking-normal')}>
                {columnDef.header}
              </p>
            </div>
          </div>
          {columnDefType !== 'group' &&
            enableColumnOrdering &&
            !isColumnPinned &&
            !allColumns.some((col) => col.columnDef.columnDefType === 'group') &&
            (columnDef.enableColumnOrdering !== false ? (
              <GrabHandleButton
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
                table={table}
              />
            ) : (
              <div className="w-7" />
            ))}
        </div>
      </div>
      {column.columns?.map((c: DataTableColumn<TData>, i) => (
        <DataTableShowHideColumnsMenuItems
          allColumns={allColumns}
          column={c}
          hoveredColumn={hoveredColumn}
          key={`${i}-${c.id}`}
          setHoveredColumn={setHoveredColumn}
          table={table}
        />
      ))}
    </>
  )
}
